/**
 * Settings — Central settings hub.
 * Route: /settings
 *
 * Sections:
 *   1. Account (email, edit profile link)
 *   2. Subscription (plan, renewal date, upgrade / manage)
 *   3. f8 memory (write-back master + per-surface toggles)
 *   4. Channels (connect whatsapp — PPL-464)
 *   5. Notification Preferences (inline — push/email toggles, categories)
 *   6. Privacy (break-the-ice invites)
 *   7. Danger Zone (delete account)
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  ArrowLeft, Check, User, Edit2, Award, Settings as SettingsIcon,
  ArrowUp, Bell, AlertTriangle, Loader, Shield, MessageCircle, Copy, ExternalLink,
} from 'react-feather';
import { useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import SEO from '../components/SEO';
import {
  getNotificationSettings,
  saveNotificationSettings,
  getAllowBreakTheIceInvites,
  setAllowBreakTheIceInvites,
  deleteAccount,
} from '../services/accountService';
import { getPremiumStatus } from '../services/premiumService';
import {
  channelErrorKind,
  createWhatsappLinkCode,
  msUntilExpiry,
  phoneFromDeepLink,
  revokeChannelLink,
  subscribeChannelLinks,
} from '../services/channelService';
import { capture } from '../services/posthog';
import {
  CATEGORY_LABELS,
  EMAIL_ONLY_CATEGORIES,
  DEFAULT_NOTIFICATION_SETTINGS,
} from '../types/account';
import type {
  NotificationSettings,
  CategorySettings,
  PushSetting,
  EmailFrequency,
  EmailSetting,
  SmartBehaviour,
} from '../types/account';
import type { PremiumStatus } from '../types/premium';
import type { ChannelErrorKind, ChannelLink, ChannelLinkCode } from '../types/channels';
import '../styles/modern.css';
import '../styles/account.css';

/**
 * Stored settings over the defaults, one level deeper (PPL-531).
 *
 * A top-level spread alone replaces the whole `categorySettings` map, so every
 * category the stored document has never carried — and most documents predate
 * rooms, circles, the review nudge and pre-meet briefs — came back `undefined`:
 * the row rendered a blank toggle and the first tap wrote a value the user had
 * not been shown. The defaults have to fill the gaps per key, which is also how
 * the server resolves them (`DEFAULT_CATEGORY_SETTINGS`).
 *
 * `intelligentPush` is carried across to `aiNudges` for the same reason the
 * server's migration does it: accounts that have not been migrated still hold
 * the old key, and their choice should survive a rename they did not make.
 */
function mergeNotifSettings(stored: NotificationSettings): NotificationSettings {
  // `intelligentPush` is pulled OUT of the spread, not just mapped over: left in
  // it would ride along into the next save and re-create a key the server's
  // migration deletes. A stored `aiNudges` still wins — it spreads after.
  const { intelligentPush, ...storedCats } = (stored.categorySettings || {}) as
    Partial<CategorySettings> & { intelligentPush?: PushSetting };
  return {
    ...DEFAULT_NOTIFICATION_SETTINGS,
    ...stored,
    categorySettings: {
      ...DEFAULT_NOTIFICATION_SETTINGS.categorySettings,
      ...(intelligentPush ? { aiNudges: intelligentPush } : {}),
      ...storedCats,
    },
  };
}

/** "7 sep" — the short, locale-driven form the write-back surfaces also use. */
function shortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }).toLowerCase();
}

/** "9:58". Rounds up so a live countdown never shows 0:00 while still valid. */
function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * What we actually know about the whatsapp link, as one value.
 *
 * `unknown` is NOT `not-connected`. A listener that errors tells us nothing new
 * about the account, and spending that ignorance as "not connected" would offer
 * a connect button to someone already linked, fire the "just connected" line at
 * them on recovery, and close their open disconnect confirm. Only a real
 * snapshot moves this off `unknown`, or between the two known states — an error
 * leaves whatever the last snapshot said standing.
 */
type ChannelWatch =
  | { status: 'unknown'; link: null }
  | { status: 'connected'; link: ChannelLink }
  | { status: 'not-connected'; link: null };

export default function Settings() {
  // ── f8 memory — root-doc `writebackEnabled` master (absent = enabled)
  // plus the per-surface map `users/{uid}/settings/writeback` with keys
  // `f8_text` / `f8_voice` (Channel names). Both halves are genuinely
  // enforced server-side since PPL-330: text at session open + a worker
  // re-check per event; voice in the proxy's readVoiceWritebackGate + the
  // same worker backstop. Calendar joins when its producer lands (PPL-274).
  const [memoryOn, setMemoryOn] = React.useState(true);
  const [memoryBusy, setMemoryBusy] = React.useState(true);
  const [surfaces, setSurfaces] = React.useState<{ f8_text: boolean; f8_voice: boolean }>({ f8_text: true, f8_voice: true });

  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (!user?.uid) { setMemoryBusy(false); return; }
    Promise.all([
      getDoc(doc(FIRESTORE_DB, 'users', user.uid)),
      getDoc(doc(FIRESTORE_DB, 'users', user.uid, 'settings', 'writeback')),
    ])
      .then(([userSnap, surfSnap]) => {
        setMemoryOn(userSnap.data()?.writebackEnabled !== false);
        const surf = surfSnap.data() || {};
        setSurfaces({ f8_text: surf.f8_text !== false, f8_voice: surf.f8_voice !== false });
      })
      .catch(() => {})
      .finally(() => setMemoryBusy(false));
  }, [user?.uid]);

  const toggleMemory = async () => {
    if (!user?.uid) return;
    const next = !memoryOn;
    setMemoryOn(next); // optimistic — a settings toggle must not lag
    try {
      await updateDoc(doc(FIRESTORE_DB, 'users', user.uid), { writebackEnabled: next });
    } catch (err) {
      console.warn('[settings] writebackEnabled toggle failed:', (err as Error)?.message);
      setMemoryOn(!next);
    }
  };

  const toggleSurface = async (key: 'f8_text' | 'f8_voice') => {
    if (!user?.uid) return;
    const next = !surfaces[key];
    setSurfaces((s) => ({ ...s, [key]: next }));
    try {
      // setDoc+merge: the settings doc may not exist yet.
      await setDoc(doc(FIRESTORE_DB, 'users', user.uid, 'settings', 'writeback'), { [key]: next }, { merge: true });
    } catch (err) {
      console.warn(`[settings] ${key} toggle failed:`, (err as Error)?.message);
      setSurfaces((s) => ({ ...s, [key]: !next }));
    }
  };

  const { isAuthenticated, redirectToLogin } = useAuthGate();
  const uid = user?.uid;

  // ─── State ────────────────────────────────────────────────────────────
  const [notifSettings, setNotifSettings] = useState<NotificationSettings>(DEFAULT_NOTIFICATION_SETTINGS);
  const [premiumStatus, setPremiumStatus] = useState<PremiumStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Privacy — break-the-ice invites from strangers (PPL-169)
  const [allowIceInvites, setAllowIceInvites] = useState(true);
  // Latest-wins guard: a slow in-flight write's failure rollback must not
  // stomp a newer toggle that has since succeeded.
  const iceInvitesReqId = useRef(0);

  // Delete account
  const [deleteStep, setDeleteStep] = useState<'idle' | 'confirm' | 'deleting'>('idle');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Debounce for notification settings
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingNotif = useRef<Partial<NotificationSettings> | null>(null);

  // ─── Auth gate ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      redirectToLogin();
    }
  }, [isAuthenticated, loading, redirectToLogin]);

  // ─── Load notification settings ──────────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    (async () => {
      setLoading(true);
      try {
        const [settings, premium, iceInvites] = await Promise.all([
          getNotificationSettings(uid),
          getPremiumStatus(uid),
          getAllowBreakTheIceInvites(uid),
        ]);
        if (settings) {
          setNotifSettings(mergeNotifSettings(settings));
        }
        setPremiumStatus(premium);
        setAllowIceInvites(iceInvites);
      } catch {
        // defaults are fine
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  // ─── Debounced save notification settings ────────────────────────────
  const saveNotif = useCallback(
    (partial: Partial<NotificationSettings>) => {
      if (!uid) return;
      pendingNotif.current = { ...(pendingNotif.current || {}), ...partial };
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        if (!pendingNotif.current) return;
        setSaving(true);
        try {
          await saveNotificationSettings(uid, pendingNotif.current);
          pendingNotif.current = null;
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        } catch {
          setError('Failed to save notification settings');
        } finally {
          setSaving(false);
        }
      }, 1000);
    },
    [uid],
  );

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // ─── Notification updaters ───────────────────────────────────────────
  const toggleMaster = (key: 'push' | 'email') => {
    const updated = { ...notifSettings, [key]: !notifSettings[key] };
    setNotifSettings(updated);
    saveNotif({ [key]: updated[key] });
  };

  const setSmartBehaviour = (value: SmartBehaviour) => {
    const updated = { ...notifSettings, smartBehaviour: value };
    setNotifSettings(updated);
    saveNotif({ smartBehaviour: value });
  };

  const setSmartDays = (value: string) => {
    const clean = value.replace(/\D/g, '').slice(0, 2);
    const updated = { ...notifSettings, smartDays: clean };
    setNotifSettings(updated);
    saveNotif({ smartDays: clean });
  };

  const toggleCategory = (key: keyof CategorySettings) => {
    const isEmailOnly = EMAIL_ONLY_CATEGORIES.includes(key);
    const current = notifSettings.categorySettings[key];
    let next: string;

    if (isEmailOnly) {
      if (key === 'weeklySummary') {
        const cycle: EmailFrequency[] = ['weekly', 'monthly', 'off'];
        const idx = cycle.indexOf(current as EmailFrequency);
        next = cycle[(idx + 1) % cycle.length];
      } else {
        next = current === 'email' ? 'off' : 'email';
      }
    } else if (key === 'directMessages') {
      // PPL-431 — direct messages are the one category with all three: push,
      // email (the only signal on web, which registers no push token), or off,
      // which silences both.
      const cycle = ['push', 'email', 'off'];
      next = cycle[(cycle.indexOf(current as string) + 1) % cycle.length];
    } else {
      next = current === 'push' ? 'off' : 'push';
    }

    const updatedCats = { ...notifSettings.categorySettings, [key]: next };
    const updated = { ...notifSettings, categorySettings: updatedCats };
    setNotifSettings(updated);
    saveNotif({ categorySettings: updatedCats });
  };

  // ─── Privacy updater ─────────────────────────────────────────────────
  const toggleIceInvites = async () => {
    if (!uid) return;
    const next = !allowIceInvites;
    const reqId = ++iceInvitesReqId.current;
    setAllowIceInvites(next);
    setSaving(true);
    try {
      await setAllowBreakTheIceInvites(uid, next);
      if (reqId !== iceInvitesReqId.current) return;
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      if (reqId !== iceInvitesReqId.current) return;
      setAllowIceInvites(!next);
      setError('Failed to save privacy settings');
    } finally {
      if (reqId === iceInvitesReqId.current) setSaving(false);
    }
  };

  // ─── Channels — whatsapp (PPL-464) ───────────────────────────────────
  //
  // The connect flow FINISHES on the user's phone, which is why the connected
  // state is a live subscription and never a fetch: the row has to flip while
  // they are looking at this page from their laptop. `unknown` = no snapshot
  // has landed yet, which is not the same as "not connected".
  const [whatsappWatch, setWhatsappWatch] = useState<ChannelWatch>({ status: 'unknown', link: null });
  const [channelWatchFailed, setChannelWatchFailed] = useState(false);
  const [codeState, setCodeState] = useState<'idle' | 'minting' | 'shown' | 'expired'>('idle');
  const [linkCode, setLinkCode] = useState<ChannelLinkCode | null>(null);
  const [msLeft, setMsLeft] = useState(0);
  const [codeCopied, setCodeCopied] = useState(false);
  const [channelError, setChannelError] = useState<ChannelErrorKind | null>(null);
  const [disconnectStep, setDisconnectStep] = useState<'idle' | 'confirm' | 'working'>('idle');
  const [disconnectFailed, setDisconnectFailed] = useState(false);
  const [justConnected, setJustConnected] = useState(false);

  const whatsappLink = whatsappWatch.link;
  const businessNumber = phoneFromDeepLink(linkCode?.deepLink || null);

  useEffect(() => {
    if (!uid) return;
    setChannelWatchFailed(false);
    return subscribeChannelLinks(
      uid,
      (links) => {
        const link = links.find((l) => l.channel === 'whatsapp') || null;
        setChannelWatchFailed(false);
        setWhatsappWatch(link ? { status: 'connected', link } : { status: 'not-connected', link: null });
      },
      () => {
        // A dead listener must not be rendered as "not connected" — that is a
        // claim about the account, and it would be a lie for someone who IS
        // linked. So the watch state is left exactly as the last snapshot set
        // it (and stays `unknown` when there has been none); only the flag
        // moves, so the waiting panel has an exit rather than spinning for a
        // flip that can't arrive.
        setChannelWatchFailed(true);
      },
    );
  }, [uid]);

  // One view event per visit, fired once a snapshot has resolved so the
  // `connected` property is true rather than merely early. A watch that fails
  // before its first snapshot fires nothing: no event beats a made-up `false`.
  const channelsViewed = useRef(false);
  useEffect(() => {
    if (loading || whatsappWatch.status === 'unknown' || channelsViewed.current) return;
    channelsViewed.current = true;
    capture('settings_channels_viewed', {
      platform: 'web',
      whatsapp_connected: whatsappWatch.status === 'connected',
    });
  }, [loading, whatsappWatch]);

  // The moment the phone side lands: close the code panel and say so. Only a
  // false → true transition celebrates; arriving already connected is not news.
  const prevConnected = useRef<boolean | null>(null);
  useEffect(() => {
    if (whatsappWatch.status === 'unknown') return;
    const connected = whatsappWatch.status === 'connected';
    const prev = prevConnected.current;
    prevConnected.current = connected;
    if (prev !== false || !connected) return;
    setCodeState('idle');
    setLinkCode(null);
    setChannelError(null);
    setJustConnected(true);
  }, [whatsappWatch]);

  // Its own effect, keyed on the flag rather than on the snapshot: a second
  // snapshot arriving inside the window would otherwise cancel the timer on
  // re-run and leave the confirmation on screen for good.
  useEffect(() => {
    if (!justConnected) return;
    const t = setTimeout(() => setJustConnected(false), 6000);
    return () => clearTimeout(t);
  }, [justConnected]);

  // A revoke's result is read from the snapshot, not from the callable, so the
  // confirm block closes on the same event that returns the row to disconnected.
  useEffect(() => {
    // Only a snapshot that says so closes it — a listener error leaves an open
    // confirm exactly where the user left it.
    if (whatsappWatch.status === 'not-connected') {
      setDisconnectStep('idle');
      setDisconnectFailed(false);
    }
  }, [whatsappWatch.status]);

  useEffect(() => {
    if (codeState !== 'shown' || !linkCode) return;
    // An unparseable expiry means no countdown — never a code declared dead on
    // arrival, which would be the one failure the user cannot work around.
    if (Number.isNaN(Date.parse(linkCode.expiresAt))) {
      setMsLeft(0);
      return;
    }
    const tick = () => {
      const left = msUntilExpiry(linkCode.expiresAt);
      setMsLeft(left);
      if (left <= 0) setCodeState('expired');
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [codeState, linkCode]);

  const startWhatsappConnect = async () => {
    setChannelError(null);
    setCodeCopied(false);
    setLinkCode(null);
    setCodeState('minting');
    try {
      setLinkCode(await createWhatsappLinkCode());
      setCodeState('shown');
    } catch (err) {
      console.warn('[channels] could not mint a whatsapp code:', (err as Error)?.message);
      setChannelError(channelErrorKind(err));
      setCodeState('idle');
    }
  };

  const cancelWhatsappConnect = () => {
    setCodeState('idle');
    setLinkCode(null);
    setChannelError(null);
  };

  const copyLinkCode = async () => {
    if (!linkCode) return;
    try {
      await navigator.clipboard.writeText(linkCode.code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1500);
    } catch { /* clipboard unavailable — the code is on screen to be typed */ }
  };

  const confirmDisconnect = async () => {
    if (!whatsappLink) return;
    setDisconnectStep('working');
    setDisconnectFailed(false);
    try {
      await revokeChannelLink(whatsappLink.linkKey);
      setDisconnectStep('idle');
    } catch (err) {
      console.warn('[channels] disconnect failed:', (err as Error)?.message);
      setDisconnectFailed(true);
      setDisconnectStep('confirm');
    }
  };

  // ─── Delete account ──────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleteStep('deleting');
    setDeleteError(null);
    try {
      await deleteAccount();
      await logout();
      navigate('/');
    } catch (err: any) {
      setDeleteError(
        err?.code === 'auth/requires-recent-login'
          ? 'For security, please log out and log back in, then try again.'
          : (err?.message || 'Account deletion failed'),
      );
      setDeleteStep('confirm');
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────

  if (!isAuthenticated) return null;

  return (
    <div className="page-shell">
      <div className="page-shell__inner page-shell__inner--narrow">
        <SEO title="Settings | pplkpr" description="Manage your account settings" path="/settings" />

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <button className="up-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} />
            <span>back</span>
          </button>
          <div className="edit-profile-save-status">
            {saving && <span className="save-indicator saving">saving...</span>}
            {saved && <span className="save-indicator saved"><Check size={12} /> saved</span>}
          </div>
        </div>

        {loading ? (
          <div className="glass-panel" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>loading settings...</div>
          </div>
        ) : (
          <>
            <h1 className="page-header__title" style={{ marginBottom: 24 }}>settings</h1>

            {error && (
              <div className="glass-panel" style={{ padding: 12, marginBottom: 16, borderColor: 'rgba(255,107,107,0.3)', color: '#ff6b6b', fontSize: 13 }}>
                {error}
              </div>
            )}

            {/* ══════════ Account Info ══════════ */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <User size={13} /> account
              </div>

              <div className="settings-row">
                <span className="settings-row-label">email</span>
                <span className="settings-row-value">{user?.email || '—'}</span>
              </div>

              <div className="settings-row">
                <span className="settings-row-label">profile</span>
                <Link to="/profile/edit" className="primary-button" style={{ padding: '6px 12px', fontSize: 12, textDecoration: 'none' }}>
                  <Edit2 size={12} /> edit profile
                </Link>
              </div>
            </div>

            {/* ══════════ Subscription ══════════ */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Award size={13} /> subscription
              </div>

              <div className="settings-row">
                <span className="settings-row-label">current plan</span>
                <span className={`premium-badge ${premiumStatus?.isPremium ? 'premium' : 'free'}`}>
                  {premiumStatus?.isPremium ? 'premium' : 'free'}
                </span>
              </div>

              {premiumStatus?.isPremium && premiumStatus?.expiry && (
                <div className="settings-row">
                  <span className="settings-row-label">renewal date</span>
                  <span className="settings-row-value">
                    {(() => {
                      const exp = premiumStatus.expiry;
                      const d = exp?.toDate ? exp.toDate() : new Date(exp);
                      return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
                    })()}
                  </span>
                </div>
              )}

              <div className="settings-row">
                <span className="settings-row-label">
                  {premiumStatus?.isPremium ? 'manage plan' : 'upgrade'}
                </span>
                <Link
                  to={premiumStatus?.isPremium ? '/settings/subscription' : '/premium'}
                  className="primary-button"
                  style={{ padding: '6px 12px', fontSize: 12, textDecoration: 'none' }}
                >
                  {premiumStatus?.isPremium ? <SettingsIcon size={12} /> : <ArrowUp size={12} />}{' '}
                  {premiumStatus?.isPremium ? 'manage' : 'go premium'}
                </Link>
              </div>
            </div>

            {/* ══════════ f8 memory (PPL-265) ══════════ */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                ✦ f8 memory
              </div>
              <div className="settings-toggle-row">
                <div>
                  <span className="settings-toggle-label">let f8 remember things from our conversations</span>
                  <span className="settings-toggle-desc">
                    f8 saves what it's sure of and shows you on the insights page,
                    where you can undo it; anything less waits for your ok there.
                    turning this off stops new suggestions — it never deletes what
                    you already saved.
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={memoryOn}
                    disabled={memoryBusy}
                    onChange={toggleMemory}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>

              {/* Per-surface toggles — keys are the backend Channel names.
                  Disabled (not hidden) under a master off: the stored values
                  survive, matching absent-means-enabled server semantics. */}
              {([
                ['f8_text', 'text chat', 'from your text conversations with f8 — both sides of them'],
                ['f8_voice', 'voice chat', 'from the transcript, not the audio'],
              ] as const).map(([key, label, desc]) => (
                <div key={key} className="settings-toggle-row" style={{ opacity: memoryOn ? 1 : 0.45, paddingLeft: 16 }}>
                  <div>
                    <span className="settings-toggle-label">{label}</span>
                    <span className="settings-toggle-desc">{desc}</span>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={memoryOn && surfaces[key]}
                      disabled={memoryBusy || !memoryOn}
                      onChange={() => toggleSurface(key)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
              ))}
            </div>

            {/* ══════════ Channels (PPL-464) ══════════ */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <MessageCircle size={13} /> channels
              </div>

              <div className="settings-row">
                <div>
                  <span className="settings-row-label">whatsapp</span>
                  {/* `label` is masked server-side ("WhatsApp · +49 ••• 1234"),
                      so it is safe to render exactly as it arrives. */}
                  <span className="settings-row-desc">
                    {whatsappLink
                      ? `${whatsappLink.label || 'whatsapp'}${whatsappLink.linkedAt ? ` · connected ${shortDate(whatsappLink.linkedAt)}` : ''}`
                      : channelWatchFailed
                        ? '—'
                        : whatsappWatch.status === 'unknown'
                          ? 'checking…'
                          : 'not connected'}
                  </span>
                </div>

                {whatsappLink
                  ? disconnectStep === 'idle' && (
                    <button className="ep-ghost-btn" onClick={() => setDisconnectStep('confirm')}>
                      disconnect
                    </button>
                  )
                  // Only a snapshot that actually says "not connected" earns a
                  // connect button. While the state is unknown — a listener that
                  // died before the first one included — neither button shows,
                  // because offering "connect" to someone already linked is the
                  // same lie the row text refuses to tell.
                  : whatsappWatch.status === 'not-connected' && codeState === 'idle' && (
                    <button
                      className="primary-button"
                      style={{ padding: '6px 12px', fontSize: 12 }}
                      onClick={startWhatsappConnect}
                    >
                      connect
                    </button>
                  )}
              </div>

              {justConnected && (
                <div className="settings-row-desc" style={{ color: 'var(--success)', marginTop: 2 }}>
                  connected — f8 answers on whatsapp now.
                </div>
              )}

              {channelError && (
                <div className="settings-row-desc" style={{ color: '#ff6b6b', marginTop: 2 }}>
                  {channelError === 'unavailable'
                    ? "whatsapp isn't available right now"
                    : "couldn't set that up just now — try again"}
                </div>
              )}

              {/* The connect panel — inline, like the delete confirm below;
                  this page has no modal of its own and does not need one. */}
              {!whatsappLink && codeState !== 'idle' && (
                <div style={{
                  marginTop: 10,
                  padding: 14,
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'rgba(255,255,255,0.65)' }}>
                    f8 answers on whatsapp with your memory. full chat is part of premium; everyone gets a taste.
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-dim)' }}>
                    runs on whatsapp&apos;s business platform — meta&apos;s terms apply and it isn&apos;t end-to-end encrypted.
                  </p>

                  {codeState === 'minting' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                      <Loader size={14} className="spin" style={{ flexShrink: 0 }} /> getting a code…
                    </div>
                  )}

                  {codeState === 'shown' && linkCode && (
                    <>
                      {/* One tap on a phone: opens whatsapp with the code
                          already typed, so it is never retyped by hand. */}
                      {linkCode.deepLink && (
                        <a
                          className="primary-button"
                          href={linkCode.deepLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '8px 14px', fontSize: 13, textDecoration: 'none', marginTop: 14,
                          }}
                        >
                          <ExternalLink size={13} /> open whatsapp
                        </a>
                      )}

                      <div style={{
                        marginTop: 12, fontSize: 12.5, color: 'rgba(255,255,255,0.55)',
                        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                      }}>
                        <span>
                          {businessNumber
                            ? `or send this code to ${businessNumber}:`
                            : 'send this code to f8 on whatsapp:'}
                        </span>
                        <code style={{
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          fontSize: 14, letterSpacing: '0.08em', color: '#EDEDED',
                        }}>
                          {linkCode.code}
                        </code>
                        <button
                          className="ep-ghost-btn"
                          style={{ padding: '3px 8px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          onClick={copyLinkCode}
                        >
                          {codeCopied ? <Check size={11} /> : <Copy size={11} />} {codeCopied ? 'copied' : 'copy'}
                        </button>
                      </div>

                      {msLeft > 0 && (
                        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-dim)' }}>
                          expires in {formatCountdown(msLeft)}
                        </div>
                      )}

                      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'rgba(255,255,255,0.5)' }}>
                        {channelWatchFailed ? (
                          <span>can&apos;t check the connection from here — send the code, then reload the page.</span>
                        ) : (
                          <>
                            <Loader size={13} className="spin" style={{ flexShrink: 0 }} /> waiting for your message…
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {codeState === 'expired' && (
                    <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>code expired</span>
                      <button
                        className="primary-button"
                        style={{ padding: '6px 12px', fontSize: 12 }}
                        onClick={startWhatsappConnect}
                      >
                        regenerate
                      </button>
                    </div>
                  )}

                  <div style={{ marginTop: 12 }}>
                    <button
                      className="ep-ghost-btn"
                      style={{ padding: '4px 10px', fontSize: 11 }}
                      onClick={cancelWhatsappConnect}
                    >
                      cancel
                    </button>
                  </div>
                </div>
              )}

              {whatsappLink && disconnectStep !== 'idle' && (
                <div style={{
                  marginTop: 10,
                  padding: 14,
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'rgba(255,255,255,0.65)' }}>
                    disconnect whatsapp? f8 stops answering there.
                  </p>
                  {disconnectFailed && (
                    <p style={{ margin: '8px 0 0', fontSize: 12.5, color: '#ff6b6b' }}>
                      couldn&apos;t disconnect just now — try again
                    </p>
                  )}
                  <div className="settings-delete-actions" style={{ marginTop: 12 }}>
                    <button
                      className="ep-ghost-btn"
                      disabled={disconnectStep === 'working'}
                      onClick={() => { setDisconnectStep('idle'); setDisconnectFailed(false); }}
                    >
                      cancel
                    </button>
                    <button
                      className="primary-button"
                      style={{ padding: '6px 12px', fontSize: 12 }}
                      disabled={disconnectStep === 'working'}
                      onClick={confirmDisconnect}
                    >
                      {disconnectStep === 'working' ? 'disconnecting…' : 'disconnect'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ══════════ Notification Preferences ══════════ */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Bell size={13} /> notifications
              </div>

              {/* Master toggles */}
              <div className="settings-toggle-row">
                <div>
                  <span className="settings-toggle-label">email notifications</span>
                  <span className="settings-toggle-desc">receive important updates via email</span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notifSettings.email}
                    onChange={() => toggleMaster('email')}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>

              {/* Smart Behaviour */}
              <div className="settings-subsection">
                <span className="settings-subsection-title">smart behaviour</span>
                <div className="settings-smart-options">
                  {(
                    [
                      { value: 'f8', label: 'f8 decides', desc: 'AI optimises your notification timing' },
                      { value: 'inactive', label: 'inactive mode', desc: 'only notify after inactivity' },
                      { value: 'mute', label: 'mute all', desc: 'silence all notifications temporarily' },
                    ] as { value: SmartBehaviour; label: string; desc: string }[]
                  ).map((opt) => (
                    <button
                      key={opt.value}
                      className={`settings-smart-btn ${notifSettings.smartBehaviour === opt.value ? 'active' : ''}`}
                      onClick={() => setSmartBehaviour(opt.value)}
                    >
                      <span className="settings-smart-btn-label">{opt.label}</span>
                      <span className="settings-smart-btn-desc">{opt.desc}</span>
                    </button>
                  ))}
                </div>

                {(notifSettings.smartBehaviour === 'inactive' || notifSettings.smartBehaviour === 'mute') && (
                  <div className="settings-smart-days">
                    <label>
                      Days:
                      <input
                        type="text"
                        className="ep-input"
                        value={notifSettings.smartDays}
                        onChange={(e) => setSmartDays(e.target.value)}
                        placeholder="e.g. 7"
                        style={{ width: 60, marginLeft: 8 }}
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Category toggles */}
              <div className="settings-subsection">
                <span className="settings-subsection-title">categories</span>
                <div className="settings-categories">
                  {(Object.keys(CATEGORY_LABELS) as (keyof CategorySettings)[]).map((key) => {
                    const isEmailOnly = EMAIL_ONLY_CATEGORIES.includes(key);
                    const value = notifSettings.categorySettings[key];
                    const isOff = value === 'off';

                    return (
                      <div key={key} className="settings-category-row">
                        <div className="settings-category-info">
                          <span className="settings-category-label">{CATEGORY_LABELS[key]}</span>
                          {isEmailOnly && <span className="settings-category-badge">email only</span>}
                        </div>
                        <button
                          className={`settings-category-toggle ${isOff ? 'off' : 'on'}`}
                          onClick={() => toggleCategory(key)}
                        >
                          {isOff ? 'Off' : value}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ══════════ Privacy ══════════ */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Shield size={13} /> privacy
              </div>

              <div className="settings-toggle-row">
                <div>
                  <span className="settings-toggle-label">let people you don't know break the ice with you</span>
                  <span className="settings-toggle-desc">
                    when off, only people you've added can invite you to a room — anyone else is told you're not taking new invites
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={allowIceInvites}
                    onChange={toggleIceInvites}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>

            {/* ══════════ Danger Zone ══════════ */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16, borderColor: 'rgba(255,107,107,0.15)' }}>
              <div className="ep-section-label" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#ff6b6b' }}>
                <AlertTriangle size={13} /> danger zone
              </div>

              {deleteStep === 'idle' && (
                <div className="settings-row">
                  <div>
                    <span className="settings-row-label">delete account</span>
                    <span className="settings-row-desc">
                      permanently delete your account and all associated data.
                    </span>
                  </div>
                  <button
                    className="ep-ghost-btn"
                    style={{ color: '#ff6b6b' }}
                    onClick={() => setDeleteStep('confirm')}
                  >
                    delete account
                  </button>
                </div>
              )}

              {deleteStep === 'confirm' && (
                <div className="settings-delete-confirm">
                  <div className="settings-delete-warning">
                    <AlertTriangle size={16} />
                    <div>
                      <h4>are you sure?</h4>
                      <p>this will permanently delete:</p>
                      <ul>
                        <li>your profile and personal data</li>
                        <li>all relationship insights and AI history</li>
                        <li>your settings and preferences</li>
                        <li>all circle memberships</li>
                      </ul>
                      <p><strong>this action cannot be undone.</strong></p>
                    </div>
                  </div>
                  {deleteError && (
                    <div className="glass-panel" style={{ padding: 10, marginBottom: 12, borderColor: 'rgba(255,107,107,0.3)', color: '#ff6b6b', fontSize: 13 }}>
                      {deleteError}
                    </div>
                  )}
                  <div className="settings-delete-actions">
                    <button className="ep-ghost-btn" onClick={() => { setDeleteStep('idle'); setDeleteError(null); }}>
                      cancel
                    </button>
                    <button
                      className="primary-button"
                      style={{ background: '#ff6b6b', boxShadow: '0 4px 16px rgba(255,107,107,0.3)' }}
                      onClick={handleDelete}
                    >
                      yes, delete my account
                    </button>
                  </div>
                </div>
              )}

              {deleteStep === 'deleting' && (
                <div className="settings-delete-progress">
                  <Loader size={20} className="spin" />
                  <span>deleting your account...</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
